import { useEffect, useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import { DateTime } from "luxon"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"

import useT from "utils/hooks/useT"
import ESignatureModal from "components/Lims/ESignatureModal"
import StatusBadge from "components/Lims/StatusBadge"
import { limsBatchHold, message } from "state_management"
import { canSign, isGmpRelaxed } from "state_management/lims/segregationOfDuties"

// QA Batch Holds (SPEC-055 R9/R10, Plannertech-user#4087/#4088; was "QA Batch Release", SPEC-013 A3)
// — the LIMS-screen home for batch/lot QUARANTINE: the active holds, and the lifted ones as history
// with their lift signature.
//
// It used to carry two more tables, "Ready to release" and "Released", which DUPLICATED the release
// flow that already lives on the Sample View (per-row Release + batch certificate in
// SampleViewScreen/SampleRowElement) and on QA Sample Review. Three screens offering the same
// QA-signed release is three places to keep in step, and three places a QA can act from without
// seeing the other two. R10 removes the duplicates here; R11 is the guarantee that nothing was lost
// with them — release and the certificate are still reachable from BOTH of the other two.
//
// The route path stays `/lims/qa-batch-release` and the `qa-release` / `coa` redirects still point
// at it, so existing bookmarks keep working (R12). Same convention as forskrifter →
// analysis-documents: the component is renamed to what it now is, the URL is left alone.
export default function QaBatchHolds() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const members = useSelector((s) => s.members)

  const [lifting, setLifting] = useState(null) // the batch-hold record pending a QA-signed lift, or null
  const [saving, setSaving] = useState(false)

  // K13 (b): active (not-yet-lifted) holds are the actionable list; lifted holds are shown as history
  // with their lift signature. The release gate (selectIsBatchRejected) already skips lifted holds.
  const activeHolds = useSelector(limsBatchHold.selectActiveHolds, shallowEqual)
  const liftedHolds = useSelector(limsBatchHold.selectLiftedHolds, shallowEqual)
  // The current member's QA gate (capability only — the per-hold four-eyes != rejectedBy is folded in
  // per row below). Drives the ghosted "Lift hold" affordance (GMP: everyone SEES it, only QA acts).
  const qaCapabilityGate = useSelector((s) => canSign(s, { capability: "qa" }), shallowEqual)
  const currentMemberId = useSelector((s) => s.auth.memberId)
  const gmpRelaxed = useSelector(isGmpRelaxed) // SPEC-025: dev-only relaxation of the four-eyes guard

  useEffect(() => {
    // SPEC-021 (B3): QC capabilities ride on the member dump (loaded app-wide) — no fetch here.
    // R10: the results / release / sampleQc / OOS fetches this screen used to make were feeding the
    // release-status selector for the two removed tables. The holds list reads none of them, so
    // opening this screen no longer pulls four collections it does not display.
    dispatch(limsBatchHold.fetch())
  }, [dispatch])

  const initials = (id) => (id && members[id] && members[id].initials) || "—"
  const fmtDate = (iso) => (iso ? DateTime.fromISO(iso).toFormat("dd/MM/yyyy HH:mm") : "—")

  // K13 (b): may the current user lift THIS hold? Needs the QA capability AND must not be the
  // person who placed the rejection (four-eyes != rejectedBy). Returns the ghost reason key, or null.
  const liftGhostKey = (hold) => {
    if (!qaCapabilityGate.allowed) return "limsLiftGhostCapability"
    // SPEC-025: skip the four-eyes (!= rejectedBy) guard when controls are relaxed (dev only).
    if (!gmpRelaxed && hold.rejectedBy && hold.rejectedBy === currentMemberId) return "limsLiftGhostFourEyes"
    return null
  }

  // K13 (b): lift the active hold under a QA signature with a MANDATORY reason. The original hold
  // record is preserved (the server stamps liftedBy/liftedAt/liftReason); a lifted hold stops
  // blocking release (the gate re-evaluates). Returns the promise so the dialog shows 403/404/409 inline.
  const handleLift = (signature) => {
    const hold = lifting
    setSaving(true)
    return dispatch(limsBatchHold.lift(hold.batchLot, signature))
      .then(() => {
        setLifting(null)
        dispatch(message.success(t("limsLiftDone", { lot: hold.batchLot })))
      })
      .finally(() => setSaving(false))
  }

  return (
    <div className="LimsQueue">
      <h1 className="LimsQueue-title">{t("limsQaBatchHolds")}</h1>

      {/* K13 (b): batch holds — the active holds (with a ghosted-for-non-QA "Lift hold" action) and,
          once lifted, the holds shown as history with their lift signature. A signed lift is a GMP
          correction: the hold record persists and the chain (rejected -> lifted) stays visible. */}
      <section className="LimsQueue-section">
        <h2 className="LimsQueue-subtitle">{t("limsBatchHoldsHeading")}</h2>
        {activeHolds.length === 0 && liftedHolds.length === 0 ? (
          <p className="LimsQueue-empty">{t("limsBatchHoldsEmpty")}</p>
        ) : (
          <Table striped hover responsive className="LimsQueue-table">
            <thead>
              <tr>
                <th>{t("limsBatchHoldLot")}</th>
                <th>{t("limsBatchHoldRejectedBy")}</th>
                <th>{t("limsBatchHoldRejectedAt")}</th>
                <th>{t("limsBatchHoldReason")}</th>
                <th>{t("limsBatchHoldStatus")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {activeHolds.map((hold) => {
                const ghostKey = liftGhostKey(hold)
                return (
                  <tr key={hold.id}>
                    <td>{hold.batchLot || "—"}</td>
                    <td>{initials(hold.rejectedBy)}</td>
                    <td>{fmtDate(hold.rejectedAt)}</td>
                    <td>{hold.reason || "—"}</td>
                    <td>
                      <StatusBadge variant="fail">{t("limsBatchHoldStatusActive")}</StatusBadge>
                    </td>
                    <td className="LimsQueue-actions">
                      <div className="LimsQueue-actions-inner">
                        <Button
                          variant="outline-warning"
                          size="sm"
                          disabled={saving || !!ghostKey}
                          title={ghostKey ? t(ghostKey) : undefined}
                          onClick={() => setLifting(hold)}
                        >
                          {t("limsLiftAction")}
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
              {liftedHolds.map((hold) => (
                <tr key={hold.id} className="is-lifted">
                  <td>{hold.batchLot || "—"}</td>
                  <td>{initials(hold.rejectedBy)}</td>
                  <td>{fmtDate(hold.rejectedAt)}</td>
                  <td>{hold.reason || "—"}</td>
                  <td>
                    <StatusBadge
                      variant="neutral"
                      title={t("limsBatchHoldLiftedDetail", {
                        by: initials(hold.liftedBy),
                        at: fmtDate(hold.liftedAt),
                        reason: hold.liftReason || "—",
                      })}
                    >
                      {t("limsBatchHoldStatusLifted")}
                    </StatusBadge>
                    <div className="LimsQueue-lift-detail">
                      {t("limsBatchHoldLiftedBy", { by: initials(hold.liftedBy), at: fmtDate(hold.liftedAt) })}
                      {hold.liftReason ? ` — ${hold.liftReason}` : ""}
                    </div>
                  </td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      {/* K13 (b): the QA lift signature — mandatory reason, four-eyes excludes the rejector. */}
      {lifting && (
        <ESignatureModal
          title={t("limsLiftAction")}
          requiredCapability="qa"
          requireReason
          excludedMemberIds={lifting.rejectedBy ? [lifting.rejectedBy] : []}
          onSign={handleLift}
          onCancel={() => setLifting(null)}
        />
      )}
    </div>
  )
}
